import { Palmtree, Search, Sparkles, UserCheck, UserPlus, UserRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { HrSurface } from './components/HrSurface'
import { KpiCard } from './components/KpiCard'
import { PersonelExportMenu } from './components/PersonelExportMenu'
import { PersonelSideWidgets } from './components/PersonelSideWidgets'
import { PersonelTable } from './components/PersonelTable'
import { usePersonelList } from './hooks/usePersonelStore'
import { personelKpis } from './lib/listStats'
import type { CalismaTuru, PersonelDurum } from './types'
import { HR_WRITE_PERM } from '../permissions'

export function PersonelListPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const navigate = useNavigate()
  const personelList = usePersonelList()
  const [q, setQ] = useState('')
  const [dep, setDep] = useState('all')
  const [durum, setDurum] = useState<'all' | PersonelDurum>('all')
  const [tur, setTur] = useState<'all' | CalismaTuru>('all')
  const [page, setPage] = useState(1)
  const kpi = useMemo(() => personelKpis(personelList), [personelList])
  const withReset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v)
      setPage(1)
    }
  const deps = useMemo(
    () => Array.from(new Set(personelList.map((p) => p.departman))).sort(),
    [personelList],
  )

  const base = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('tr')
    return personelList.filter((p) => {
      if (dep !== 'all' && p.departman !== dep) return false
      if (tur !== 'all' && p.calisma_turu !== tur) return false
      if (!needle) return true
      return (
        p.ad.toLocaleLowerCase('tr').includes(needle) ||
        p.sicil.toLocaleLowerCase('tr').includes(needle) ||
        p.pozisyon.toLocaleLowerCase('tr').includes(needle) ||
        p.email.toLocaleLowerCase('tr').includes(needle)
      )
    })
  }, [personelList, q, dep, tur])
  const rows = useMemo(() => (durum === 'all' ? base : base.filter((p) => p.durum === durum)), [base, durum])
  const counts = useMemo(
    () => ({
      all: base.length,
      aktif: base.filter((p) => p.durum === 'aktif').length,
      izinli: base.filter((p) => p.durum === 'izinli').length,
      deneme: base.filter((p) => p.durum === 'deneme').length,
    }),
    [base],
  )

  return (
    <HrSurface>
      <PageHeader
        title={t('hr.personel.listTitle')}
        description={t('hr.personel.listSubtitle')}
        actions={
          <>
            <PersonelExportMenu filtered={rows} all={personelList} />
            {can(HR_WRITE_PERM) ? (
              <Button size="sm" className="shadow-sm" onClick={() => navigate('/hr/personel/yeni')}>
                <UserPlus />
                {t('hr.personel.add')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <KpiCard
          label={t('hr.personel.kpi.toplam')}
          value={kpi.toplam}
          hint={t('hr.personel.kpi.hint.toplam', { n: kpi.departman })}
          tint="info"
          icon={<UserRound className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.kpi.aktif')}
          value={kpi.aktif}
          hint={t('hr.personel.kpi.hint.aktif', { pct: kpi.aktifPct })}
          tint="success"
          icon={<UserCheck className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.kpi.izinli')}
          value={kpi.izinli}
          hint={t('hr.personel.kpi.hint.izinli')}
          tint="warning"
          icon={<Palmtree className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.kpi.yeni')}
          value={kpi.yeni}
          hint={t('hr.personel.kpi.hint.yeni')}
          tint="accent"
          icon={<Sparkles className="size-5" />}
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <Card className="overflow-hidden rounded-2xl border-border/70 shadow-sm">
          <CardHeader className="flex-col items-stretch gap-3 border-b bg-card/80 p-4 pb-4">
            <Tabs value={durum} onValueChange={(v) => withReset(setDurum)(v as 'all' | PersonelDurum)}>
              <TabsList aria-label={t('hr.personel.filters.durum')} className="max-w-full overflow-x-auto scrollbar-none">
                {(['all', 'aktif', 'izinli', 'deneme'] as const).map((v) => (
                  <TabsTrigger key={v} value={v}>
                    {v === 'all' ? t('common.all') : t(`hr.personel.durum.${v}`)}
                    <span className="rounded-full bg-muted px-1.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
                      {counts[v]}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-0 flex-[1_1_16rem]">
                <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="h-10 rounded-xl bg-muted/40 pl-9 shadow-none"
                  value={q}
                  onChange={(e) => withReset(setQ)(e.target.value)}
                  placeholder={t('hr.personel.searchPlaceholder')}
                />
              </div>
              <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
                <Select
                  size="lg"
                  className="w-full min-w-0 rounded-xl sm:w-auto sm:min-w-[9.5rem]"
                  value={dep}
                  onChange={withReset(setDep)}
                  aria-label={t('hr.personel.filters.departman')}
                  options={[
                    { value: 'all', label: t('hr.personel.filters.allDeps') },
                    ...deps.map((d) => ({ value: d, label: d })),
                  ]}
                />
                <Select
                  size="lg"
                  className="w-full min-w-0 rounded-xl sm:w-auto sm:min-w-[9.5rem]"
                  value={tur}
                  onChange={(v) => withReset(setTur)(v as 'all' | CalismaTuru)}
                  aria-label={t('hr.personel.filters.tur')}
                  options={[
                    { value: 'all', label: t('hr.personel.filters.allTur') },
                    { value: 'tam_zamanli', label: t('hr.personel.calisma.tam_zamanli') },
                    { value: 'hibrit', label: t('hr.personel.calisma.hibrit') },
                    { value: 'yari_zamanli', label: t('hr.personel.calisma.yari_zamanli') },
                  ]}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <PersonelTable rows={rows} page={page} onPage={setPage} />
          </CardContent>
        </Card>

        <PersonelSideWidgets list={personelList} />
      </div>
    </HrSurface>
  )
}
